import { useMemo } from 'react';
import {
    ACTIVE_ADMISSION_STATUSES,
    CLOSED_OUTCOMES,
} from '../constants/admissionStatus';

const DAY_MS = 86_400_000;
const APPROVED_OUTCOMES = new Set(['Admitted', 'Insurance Approved']);
const DENIED_OUTCOMES = new Set(['Insurance Denied', 'Rejected']);

function normalizeStatus(raw) {
    if (raw == null || raw === '') return 'In progress';
    return raw;
}

function isClosed(rawStatus) {
    return rawStatus === 'Closed';
}

export function useCaseMetrics(cases) {
    return useMemo(() => {
        const safe = Array.isArray(cases) ? cases : [];

        const counts = Object.fromEntries(
            ACTIVE_ADMISSION_STATUSES.map((s) => [s, 0])
        );
        const closedByOutcome = Object.fromEntries(
            CLOSED_OUTCOMES.map((s) => [s, 0])
        );
        let totalClosed = 0;
        let ageSumMs = 0;
        let ageCount = 0;
        let approvedCount = 0;
        let deniedCount = 0;
        let closeDurationSumMs = 0;
        let closeDurationCount = 0;

        for (const c of safe) {
            const raw = c?.admission_status;
            if (isClosed(raw)) {
                totalClosed += 1;
                const outcome = c?.terminal_outcome || 'Closed';
                if (outcome in closedByOutcome) {
                    closedByOutcome[outcome] += 1;
                }
                if (APPROVED_OUTCOMES.has(outcome)) approvedCount += 1;
                else if (DENIED_OUTCOMES.has(outcome)) deniedCount += 1;

                const createdAt = c?.createdAt;
                const archivedAt = c?.archived_at;
                if (
                    createdAt && typeof createdAt.toMillis === 'function'
                    && archivedAt && typeof archivedAt.toMillis === 'function'
                ) {
                    const duration = archivedAt.toMillis() - createdAt.toMillis();
                    if (duration >= 0) {
                        closeDurationSumMs += duration;
                        closeDurationCount += 1;
                    }
                }
                continue;
            }
            const status = normalizeStatus(raw);
            if (counts[status] == null) {
                continue; // unknown active status — silently skip
            }
            counts[status] += 1;

            const createdAt = c?.createdAt;
            if (createdAt && typeof createdAt.toMillis === 'function') {
                ageSumMs += Date.now() - createdAt.toMillis();
                ageCount += 1;
            }
        }

        const histogram = ACTIVE_ADMISSION_STATUSES.map((status) => ({
            status,
            count: counts[status],
        }));
        const activeCount = histogram.reduce((n, b) => n + b.count, 0);

        let avgOpenAgeDays = null;
        if (ageCount > 0) {
            const avgMs = ageSumMs / ageCount;
            avgOpenAgeDays = Math.round((avgMs / DAY_MS) * 10) / 10;
        }

        let approvalRatePct = null;
        const decidedCount = approvedCount + deniedCount;
        if (decidedCount > 0) {
            approvalRatePct = Math.round((approvedCount / decidedCount) * 100);
        }

        let avgTimeToCloseDays = null;
        if (closeDurationCount > 0) {
            const avgMs = closeDurationSumMs / closeDurationCount;
            avgTimeToCloseDays = Math.round((avgMs / DAY_MS) * 10) / 10;
        }

        return {
            histogram,
            avgOpenAgeDays,
            totalClosed,
            activeCount,
            closedByOutcome,
            approvalRatePct,
            avgTimeToCloseDays,
        };
    }, [cases]);
}
